<template>
  <div class="player__container" :loading="loading">
    <button
      class="player__button"
      :disabled="loading"
      role="button"
      tabindex="0"
      @click="toggleAudio"
      @keyup.enter="toggleAudio"
    >
      <WIcon :icon="audioAction" size="large" />
    </button>

    <div ref="container" class="bar__container">
      <input
        v-model="percentage"
        class="bar__slider"
        type="range"
        max="100"
        @input="onRangeInput"
        @change="setPlayerCurrentTime"
      />
    </div>

    <ul class="player__timers" :disabled="loading">
      <li>{{ currentTime }}</li>
      <li>{{ durationTime }}</li>
    </ul>

    <audio
      ref="player"
      :src="source"
      muted
      @timeupdate="updateCurrentTime"
      @loadeddata="onAudioLoad"
      @play="onPlay"
      @pause="onPause"
      @ended="onAudioEnd"
    />

    <button
      size="small"
      class="player__playback-rate"
      :disabled="loading"
      @click="updatePlaybackRate"
    >
      {{ playbackRate }}x
    </button>
  </div>
</template>

<script>
import { WIcon } from '@alice-health/wonderland-vue'

import { PlaybackRate } from '@/models/audio'
import { formatAudioTime } from '@/utils/time'

export default {
  components: {
    WIcon
  },
  props: {
    source: { type: String, default: undefined }
  },
  emits: ['play'],
  data() {
    return {
      isPlaying: false,
      percentage: 0,
      currentTime: '--:--',
      durationTime: '--:--',
      player: undefined,
      playbackRate: PlaybackRate.DEFAULT
    }
  },
  computed: {
    loading() {
      return !this.source || null
    },
    audioAction() {
      if (this.loading) return 'icLoad'
      if (this.isPlaying) return 'icPauseCircle'
      return 'icPlayCircle'
    }
  },
  watch: {
    percentage() {
      this.$refs.container.style.setProperty(
        '--progress-width',
        `${this.percentage}%`
      )
    }
  },
  mounted() {
    this.player = this.$refs.player
  },
  methods: {
    toggleAudio() {
      if (this.isPlaying) this.pause()
      else this.player.play()
    },
    updateCurrentTime() {
      this.currentTime = formatAudioTime(this.player.currentTime)
      this.percentage = this.player.duration
        ? (this.player.currentTime / this.player.duration) * 100
        : 0
    },
    onPlay() {
      this.player.muted = false
      this.$emit('play')
      this.isPlaying = true
    },
    pause() {
      this.player.pause()
    },
    onPause() {
      this.isPlaying = false
    },
    onAudioLoad() {
      this.durationTime = formatAudioTime(parseInt(this.player.duration))
      this.currentTime = formatAudioTime(parseInt(this.player.currentTime))
    },
    onAudioEnd() {
      this.isPlaying = false
    },
    setPlayerCurrentTime() {
      this.player.play()
    },
    onRangeInput() {
      this.player.currentTime = (this.percentage * this.player.duration) / 100
      this.currentTime = formatAudioTime(this.player.currentTime)
      this.player.pause()
    },
    updatePlaybackRate() {
      if (this.playbackRate === PlaybackRate.DEFAULT) {
        this.playbackRate = PlaybackRate.FAST
      } else if (this.playbackRate === PlaybackRate.FAST) {
        this.playbackRate = PlaybackRate.FASTEST
      } else {
        this.playbackRate = PlaybackRate.DEFAULT
      }

      this.player.playbackRate = this.playbackRate
    }
  }
}
</script>

<style lang="scss">
:root,
[data-theme='on-light'] {
  --progress-width: 0%;
  --base-player-color: var(--sys-color-surface-background-light);
  --audio-progress-background: var(--sys-color-surface-background-light);
  --audio-play-color: var(--sys-color-surface-background-light);
  --audio-playback-color: var(--gl-color-shades-magenta-60);
  --audio-timer-color: var(--sys-color-surface-background-light);
  --audio-border-rate-color: transparent;
}
</style>

<style scoped lang="scss">
@import '@/style/_non-rendering';

@keyframes placeholderAnimate {
  0% {
    background-position: -650px 0;
  }
  100% {
    background-position: 650px 0;
  }
}

.player {
  &__button {
    @extend %reset-button;
    color: var(--audio-play-color);
    cursor: pointer;

    &[disabled] {
      opacity: 0.5;
      cursor: not-allowed;
      animation: spin 1s infinite linear;
    }
  }

  &__container {
    display: grid;
    grid-template-columns: 24px 1fr 46px;
    align-items: center;
    column-gap: 16px;
    position: relative;
    z-index: 2;
    max-width: max-content;

    &[loading] {
      --base-player-color: linear-gradient(
        to right,
        var(--gl-color-shades-magenta-00) 2%,
        var(--sys-color-surface-background-light) 18%,
        var(--gl-color-shades-magenta-00) 33%
      );
    }

    audio {
      display: none;
    }
  }

  &__playback-rate {
    grid-column: 3;
    grid-row: 1/3;
    background: var(--sys-color-surface-background-light);
    color: var(--audio-playback-color);
    width: 46px;
    height: 26px;
    align-self: flex-start;
    border-color: transparent;
    border-radius: var(--gl-border-radius-lg);
    border: var(--gl-border-width-xs) solid var(--audio-border-rate-color);

    &[disabled] {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

.bar {
  &__container {
    width: 100%;
    position: relative;
    cursor: pointer;

    input[type='range'] {
      -webkit-appearance: none;
      background-color: var(--audio-progress-background);
      padding: 0;
      border: 0;
      height: 2px;
      cursor: pointer;

      &:focus-visible::-webkit-slider-runnable-track {
        --base-player-color: var(--gl-color-shades-magenta-00);
      }

      &::-webkit-slider-runnable-track {
        -webkit-appearance: none;
        background: linear-gradient(
          to right,
          var(--base-player-color) var(--progress-width),
          transparent var(--progress-width)
        );
        border-radius: 2px;
        height: 2px;
      }

      &::-webkit-slider-thumb {
        -webkit-appearance: none;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: var(--base-player-color);
        margin-top: -3px;
      }

      &::-moz-range-progress {
        background-color: var(--base-player-color);
        border-radius: 2px;
      }

      &::-moz-range-thumb {
        border: 0;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: var(--base-player-color);
      }
    }
  }
}

.player__timers {
  @extend %caption;
  grid-column: 2/3;
  display: flex;
  list-style: none;
  padding: 0;
  justify-content: space-between;
  color: var(--audio-timer-color);
  padding-top: var(--gl-spacing-02);
  line-height: 12px;

  &[disabled] {
    opacity: 0.5;
  }
}
</style>
